<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";

import { useWebsiteStore } from "@/stores/website";
import { useUserStore } from "@/stores/user";
import Treeview from "@/components/PagePicker/Treeview.vue";

const userStore = useUserStore();
const {user} = storeToRefs(userStore);
const websiteStore = useWebsiteStore();
const {website} = storeToRefs(websiteStore);
const emit = defineEmits(["start-scan", "select-page"]);

const urlSearch = ref("");

let tree = ref([])


const props = defineProps({
  pageList: Array,
  showSearch: {
    type: Boolean,
    default: true
  },
  forceRootSelection: {
    type: Boolean,
    default: false,
  },
  suggestPages: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: ''
  },
  parentPath: {
    type: String,
    default: 'dashboard'
  }
});


const selectedPaths = ref([])

watch(selectedPaths, (newVal) => {
  emit('select-page', newVal)
}, { deep: true, immediate: true })


function convertPathsToTree(paths) {

  let nextNodeId = 0;
  // Create the root node
  const root = {
    name: '/',
    children: [],
    level: 0,
    parentPath: '',
    fullPath: '',
    page: null,
    showLimit: 6,
  };

  // Process each path
  paths.forEach(item => {
    // Remove leading slash and split by slashes
    const pathParts = item.path.replace(/^\//, '').split('/');

    // Start at the root
    let currentNode = root;

    // Navigate through each part of the path

    pathParts.forEach((part, index) => {
      let currentFlatListEntry = flatList.value.find((p) => p.fullPath === currentNode.fullPath + '/' + part);
      // If this part doesn't exist in the current node's children, create it
      if (!(currentNode.children && currentNode.children[part])) {
        currentNode.children[part] = {
          name: part,
          children: [],
          parentPath: currentNode.fullPath,
          level: index + 1,
          fullPath: currentNode.fullPath + '/' + part,
          page: null,
          showLimit: currentFlatListEntry ? currentFlatListEntry.showLimit : 5,
        };
      }

      // Move to the next node

      currentNode = currentNode.children[part];
      if (!currentNode.children){
        currentNode.children = []
      }

      // If this is the last part of the path, set the external ID
      if (index === pathParts.length - 1) {
        currentNode.page = item
      }
    });
  });

  // Helper function to convert children objects to arrays for a cleaner output
  function normalizeTree(node, parent) {
    const result = {
      name: node.name,
      parentPath: node.parentPath,
      fullPath: node.fullPath,
      level: node.level,
      page: node.page,
      url: node.page && node.page.url,
      showLimit: node.showLimit,
      visible: true,
      selected: (props.forceRootSelection && node.fullPath === '/') || selectedPaths.value.indexOf(node.page?.url) !== -1,
      children: [],
      parent: parent,
    };


    const childrenArray = Object.values(node.children);

    if (childrenArray.length > 0) {
      childrenArray.sort((a, b) => {
        if (a.fullPath < b.fullPath) {
          return -1;
        }
        if (a.fullPath > b.fullPath) {
          return 1;
        }
        return 0;
      });
      result.children = childrenArray.map(child => normalizeTree(child, result));
    }


    return result;
  }

  return normalizeTree(root, null);
}


let flatList = ref([])
watch(
    () => props.pageList, value => {
      calcTree()
    },
    {
      immediate: true,
    }
)

function flattenTree(tree) {
  if (tree.children) {
    for (let entry of tree.children) {

      if (entry.children) {
        if (entry.children.length === 1) {
          if (entry.children[0].page) {
            entry.page = entry.children[0].page
            entry.fullPath = entry.children[0].fullPath
            entry.url = entry.children[0].url
            entry.children = []
            entry.selected = (props.forceRootSelection && entry.fullPath === '/') || selectedPaths.value.indexOf(entry.page?.url) !== -1
          }
        }
      }
      flattenTree(entry)
    }
  }
}

function seperateDirectoryPages(tree){
  if (tree.children && tree.children.length > 0) {
    if (tree.page){  // this entry has both children and is a page, so move out the page
      let newEntry = Object.assign({}, tree)
      newEntry.children = []
      tree.children.unshift(newEntry)


      tree.page = null;
      tree.url = ''
      
    }
    for (let entry of tree.children) {

      if (entry.children) {
        if (entry.children.length === 1) {
          if (entry.children[0].page) {
            entry.page = entry.children[0].page
            entry.fullPath = entry.children[0].fullPath
            entry.children = []
          }
        }
      }
      seperateDirectoryPages(entry)
    }
  }
}

function calcTree() {
  let filtered = props.pageList.filter((p) => p.url.toLowerCase().includes(urlSearch.value.toLowerCase()));
  tree.value = convertPathsToTree(filtered);
  flattenTree(tree.value)
  flattenTree(tree.value)
  flattenTree(tree.value)
  flattenTree(tree.value)
  flattenTree(tree.value)
  flattenTree(tree.value)

  seperateDirectoryPages(tree.value)
}

const sortedChildren = computed(() => {
  return tree.value.children
});

function selectedChildren(root) {

  let r = []
  if (root.page?.url && root.selected) {
    r.push(root.page?.url)
  }
  if (root.children) {
    for (const child of root.children) {
      // r.push(child.page?.url)
      r.push(...selectedChildren(child));
    }
  }
  return r
}

function selectNode(root, selected, preserveChecked = false) {
  if (root.page) {
    if (['pending', 'in_progress'].includes(root.page.status)) {
      return
    }
  }

  let finalSelected = selected;
  if (preserveChecked && root.page?.checked) {
    finalSelected = true;
  }

  root.selected = finalSelected;
  const index = selectedPaths.value.indexOf(root.page?.url)

  if (root.page) {
    if (finalSelected) {
      if (index === -1) {
        selectedPaths.value.push(root.page.url)
      }
    } else {
      if (index !== -1) {
        selectedPaths.value.splice(index, 1)
      }
    }
  }
  if (root.children) {
    for (const child of root.children) {
      selectNode(child, selected, preserveChecked);
    }
  }
}

function selectAll(selected, preserveChecked = false) {

  for (let child of sortedChildren.value) {

    let entrySelected = selected
    if (props.forceRootSelection && child.fullPath === '/') {
      entrySelected = true;
    }
    selectNode(child, entrySelected, preserveChecked)
  }
}

function suggestPath(topLevelPaths, path){
  if (topLevelPaths.filter(p=>p.selected).count >= 5){
    return
  }
  let p = topLevelPaths.find(p=>p.fullPath === path)
  if (p){
    selectNode(p, true, true)
  }
}

function selectSuggestedPages(){
  let i =0;
  let topLevelPaths = sortedChildren.value.filter(c=>c.level === 1 && c.page)

  suggestPath(topLevelPaths,'/about')
  suggestPath(topLevelPaths,'/pricing')
  suggestPath(topLevelPaths,'/product')
  suggestPath(topLevelPaths,'/team')
  suggestPath(topLevelPaths,'/faq')
  suggestPath(topLevelPaths,'/products')
  suggestPath(topLevelPaths,'/features')
  suggestPath(topLevelPaths,'/contact')
  suggestPath(topLevelPaths,'/questions')
  suggestPath(topLevelPaths,'/frequently-asked-questions')
  suggestPath(topLevelPaths,'/solutions')
  suggestPath(topLevelPaths,'/services')
  suggestPath(topLevelPaths,'/career')
  suggestPath(topLevelPaths,'/careers')
  suggestPath(topLevelPaths,'/case-studies')
  suggestPath(topLevelPaths,'/case-study')

}

onMounted(() => {
  selectAll(false, true)
  if (props.suggestPages){
    selectSuggestedPages()
  }

})
const selectAllInput = ref(false)

function clearSelection() {
  selectAll(false)
  selectAllInput.value = false
}

defineExpose({
  clearSelection
})

</script>

<template>

  <div class="row g-4" v-if="title">
    <div class="col-7">
      <h4>{{ title }}</h4>
    </div>

  </div>

  <div class="row">
    <div class="col">
      <div class="seo-report-entry-header">

        <div class="row">
          <div class="col d-flex align-items-center">
          <span class="checkbox">
            <input v-model="selectAllInput" @change="selectAll(selectAllInput)" type="checkbox"
                   class="form-check-input me-3">
          </span>
            <label @click="selectAllInput=!selectAllInput;selectAll(selectAllInput)">URL:</label>
          </div>

          <div class="col text-end me-2">
            <div style="text-align: right" v-if="showSearch">
              <div class="input-group mb-1">
        <span class="input-group-text" style="background-color:white">
          <i class="fa fa-search"></i></span>
                <input type="text"
                       v-model="urlSearch"
                       @keyup="calcTree"
                       class="form-control"
                       placeholder="Filter.."
                       id="example-group2-input1"
                       name="example-group2-input1">
                <div v-show="urlSearch"
                     title="Clear Filter"
                     class="fa fa-times position-absolute top-50 end-0 translate-middle-y me-3"
                     style="cursor: pointer"
                     @click="urlSearch=''; calcTree()">
                </div>
              </div>
            </div>
          </div>
        </div>


      </div>

      <div class="select-pages-div">
        <div v-for="child in sortedChildren" :key="child.name">
          <Treeview :force-root-selection="forceRootSelection" :root="child" :parent-path="parentPath" :selected-paths="selectedPaths"/>
        </div>
      </div>
    </div>

  </div>


</template>
<style>
  .seo-report-entry-header {
    background-color: white;
    padding-left: 12px;
    padding-bottom: 6px;
    padding-top: 6px;
    border-bottom: 1px solid #f7f7f7;

  }
.select-pages-div {
  max-height: calc(-310px + 100vh);
  overflow: auto;


}
</style>